const SCROLL_DURATION_MS = 500;

export const scrollIntoView = (
  targetElement: HTMLElement | null,
  scrollableContainer: HTMLElement | null,
  focusInput: boolean = false,
): { cancel?: () => void } => {
  if (targetElement === null || scrollableContainer === null) {
    return { cancel: undefined };
  }

  // Get the field's position relative to the scrollable container
  const targetRect = targetElement.getBoundingClientRect();
  const containerRect = scrollableContainer.getBoundingClientRect();

  // Calculate the target scroll position to center the field
  const targetRelativeTop = targetRect.top - containerRect.top + scrollableContainer.scrollTop;
  const targetScrollTop = targetRelativeTop - scrollableContainer.clientHeight / 2 + targetRect.height / 2;

  // Custom smooth scroll implementation
  const startScrollTop = scrollableContainer.scrollTop;
  const scrollDistance = targetScrollTop - startScrollTop;
  const startTime = performance.now();

  const animateScroll = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / SCROLL_DURATION_MS, 1);

    // Easing function for smooth animation (ease-in-out)
    const easeInOut = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;

    scrollableContainer.scrollTop = startScrollTop + scrollDistance * easeInOut;

    if (progress < 1) {
      window.requestAnimationFrame(animateScroll);
    } else {
      // Focus the appropriate element after scrolling completes
      if (focusInput) {
        // Look for elements marked as focusable first, then fall back to standard form inputs
        const focusableElement = targetElement.querySelector('[data-focusable="true"]') as HTMLElement;
        const inputElement = targetElement.querySelector('input, textarea, select') as HTMLElement;

        const elementToFocus = focusableElement || inputElement;
        if (elementToFocus) {
          elementToFocus.focus();
        }
      }
    }
  };

  const requestId = window.requestAnimationFrame(animateScroll);
  return {
    cancel: () => {
      window.cancelAnimationFrame(requestId);
    },
  };
};
